import Box from '@redbubble/design-system/src/Box';
import React, { CSSProperties, ReactNode } from 'react';
import styles from './Picture.module.scss';

interface PictureProps {
  ratioPaddingClass?: string;
  widthClass?: string;
  roundedCorners?: boolean;
  fillContainer?: boolean;
  children: ReactNode;
}

interface ImgProps {
  src: string;
  alt: string;
  loading?: 'eager' | 'lazy';
  style?: CSSProperties;
  className?: string;
  ariaHidden?: boolean;
}

const Img: React.FC<ImgProps> = ({
  src,
  alt,
  loading = 'lazy',
  style,
  className = styles.fullWidth,
  ariaHidden = false,
}) => {
  return (
    // cannot use next image in picture element
    <img
      src={src}
      style={style}
      className={className}
      alt={alt}
      loading={loading}
      aria-hidden={ariaHidden}
    />
  );
};

interface SourceProps {
  srcSet: string;
  type: string;
  media?: string;
  sizes?: string;
  testId?: string;
}

const Source: React.FC<SourceProps> = ({
  srcSet,
  type,
  media,
  sizes,
  testId,
}) => {
  return (
    <source
      srcSet={srcSet}
      type={type}
      media={media && media}
      sizes={sizes && sizes}
      data-testid={testId}
    />
  );
};

interface PictureComposition {
  Img: React.FC<ImgProps>;
  Source: React.FC<SourceProps>;
}

export const Picture: React.FC<PictureProps> & PictureComposition = ({
  ratioPaddingClass,
  widthClass,
  roundedCorners = false,
  fillContainer = false,
  children,
}) => {
  if (fillContainer) {
    return (
      <picture
        className={[
          styles.picture,
          styles.fillContainer,
          roundedCorners ? styles.rounded : '',
        ].join(' ')}
        data-testid="picture"
      >
        {children}
      </picture>
    );
  }

  const ratioOuterClasses = [styles.ratioOuter, widthClass].join(' ');

  return (
    <Box className={ratioOuterClasses}>
      <Box className={ratioPaddingClass}>
        <Box className={styles.ratioInner}>
          <picture
            className={[
              styles.picture,
              roundedCorners ? styles.rounded : '',
            ].join(' ')}
            data-testid="picture"
          >
            {children}
          </picture>
        </Box>
      </Box>
    </Box>
  );
};

Picture.Img = Img;
Picture.Source = Source;
